// Каталог колонок табеля — то единственное, чем табель отличается от ведомости в общей сетке.
//
// Все четыре поведения ячейки встречаются здесь разом (правило 6): день вводится, итог строки
// считается, фамилия читается из карточки человека, закрытый период блокирован с причиной.

import type { CellState, GridColumn } from '@/shared/grid';
import type { CellOut, GridRowOut } from '@/shared/api/endpoints/timesheet';
import { formatHours, sumHours } from '@/shared/hours/format';
import { todayIso } from '@/shared/ui/date-field';
import { cn } from '@/utils/cn';

/** Короткие подписи отметок. Буква в ячейке — так табель и читают глазами. */
export const MARK_LETTER: Record<string, string> = {
  absent: 'Н',
  sick: 'Б',
  day_off: 'О',
};

export const MARK_TITLE: Record<string, string> = {
  worked: 'Отработано',
  absent: 'Невыход',
  sick: 'Больничный',
  day_off: 'Отгул',
};

/** Буква из ячейки в отметку. Табель так и читают глазами: Н, Б, О. */
export const LETTER_TO_MARK: Record<string, 'absent' | 'sick' | 'day_off'> = {
  Н: 'absent',
  Б: 'sick',
  О: 'day_off',
  н: 'absent',
  б: 'sick',
  о: 'day_off',
};

const ДНИ_НЕДЕЛИ = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];

/**
 * Итог в подвале: сумма часов ТЕМИ ЖЕ строками, что пришли с сервера, и в том же виде, что
 * соседние числа сетки.
 *
 * Раньше подвалы складывали часы через `Number(...)`, то есть во float, а один из них ещё и
 * печатал результат сам: `String(Math.round(сумма * 100) / 100)` давал «36.3» — с точкой,
 * без разделителя разрядов, рядом с «1 234,5» в соседней колонке. Часы приходят строкой
 * именно для того, чтобы этого не было (ПРАВИЛА.md, раздел 3), и готовое точное сложение
 * лежит рядом, в `shared/hours/format.ts`.
 *
 * Пустой список значений — это НЕ ноль: за день никто не отметился, и «0» здесь утверждал бы,
 * что все вышли и отработали по нулю часов. Разводим прочерком (правило 7).
 */
function итогЧасов(значения: readonly (string | null | undefined)[]): string {
  const есть = значения.filter((часы): часы is string => часы !== null && часы !== undefined);
  return есть.length === 0 ? formatHours(null) : formatHours(sumHours(есть));
}

function ячейкаДня(строка: GridRowOut, день: string): CellOut | undefined {
  return строка.cells.find((я) => я.work_date === день);
}

/** Входит ли день в срок назначения: до приезда и после отъезда ячейки не существует. */
function вСрокеНазначения(строка: GridRowOut, день: string): boolean {
  if (день < строка.date_from) {
    return false;
  }
  return строка.date_to === null || день <= строка.date_to;
}

/**
 * Что с объектом в этот день: сколько вышло, сколько положено и на сколько разошлось.
 *
 * Считается ОДИН раз на весь табель и раздаётся колонкам готовым: тридцать колонок, каждая
 * складывающая тринадцать строк сама, — это тридцать проходов по сетке на каждую перерисовку.
 */
export interface СостояниеДня {
  readonly факт: number;
  readonly норма: number;
  /** Норма минус факт. Больше нуля — объект не закрыт, меньше — переработка. */
  readonly разница: number;
  /**
   * День ещё не наступил, и «факт» в нём — это нарисованный график, а не отработанное. Слова
   * тогда другие: «по графику 18 из 24», а не «вышло 18 из 24» — не вышел ещё никто.
   */
  readonly будущий: boolean;
}

export type СостояниеДней = ReadonlyMap<string, СостояниеДня>;

/**
 * Разложить недобор месяца по дням.
 *
 * ## Почему сравниваем ДЕНЬ, а не ячейку
 *
 * Заказчик 21.09.2026: «создали график вручную и понимаем, что не хватает 12 часов, а где
 * именно это искать?». Напрашивающийся ответ — сравнить факт ячейки с планом — на его данных
 * не работает вовсе: график он заполнял руками, и плана в ячейках нет ни одного.
 *
 * Зато норма у объекта суточная и постоянная: 24 часа в сутки по постам, 720 за тридцать дней.
 * Значит день, где вышло 18 вместо 24, — это шесть часов, которые пост стоял незакрытым, и
 * найти их можно, ничего не зная про то, кто именно должен был выйти. Два таких дня дают
 * ровно те 12 часов, что стоят в плитке наверху.
 *
 * ## Виновника называем только когда он доказуем
 *
 * Ячейка обводится отдельно лишь там, где это следует из данных: есть план и факт меньше
 * плана, либо стоит отметка невыхода. Угадывать виновника по соседним дням нельзя — ложный
 * красный приучает не смотреть на красное вообще, и тогда пропадёт и настоящий.
 */
export function состояниеДней(
  rows: readonly GridRowOut[],
  days: readonly string[],
  normPerDay: string | null | undefined,
  { естьСмены = true, сегодня = todayIso() }: { естьСмены?: boolean; сегодня?: string } = {},
): СостояниеДней {
  const норма = normPerDay === null || normPerDay === undefined ? null : Number(normPerDay);
  if (норма === null || !Number.isFinite(норма) || норма <= 0) {
    // Норматива нет — сравнивать не с чем. У 24 объектов заказчика из 60 он не заполнен, и
    // красить им весь месяц значило бы назвать дефектом то, чего мы просто не знаем.
    return new Map();
  }
  if (!естьСмены) {
    // Пустой табель — это НЕ месяц с дырами, это месяц, который ещё не вели. Красить его
    // целиком значило бы сказать «объект не охранялся тридцать дней» там, где правда другая:
    // «табельщик ещё не отмечал». Ложный красный приучает не смотреть на красное вообще, и
    // тогда пропадёт и настоящий.
    return new Map();
  }
  // ## Докуда сравнивать: до сегодня ИЛИ до конца нарисованного графика
  //
  // 22.09.2026 здесь стоял запрет сравнивать дни позже сегодняшнего: иначе табель текущего
  // месяца каждое первое число краснел бы весь, до тридцатого. Запрет был верным для пустых
  // дней и неверным для случая самого заказчика: график он рисует НАПЕРЁД. Его жалоба была
  // про 27 и 28 сентября по 18 часов — и после запрета именно эти дни перестали краснеть,
  // а плитка «Недобор 12 ч» горела над ними по-прежнему (сверка замечаний 23.09.2026).
  //
  // Поэтому граница — последний день, до которого график уже нарисован, если он позже
  // сегодняшнего. Пустые дни ЗА этой границей — «ещё не рисовали», а не дыра в охране. Пустой
  // день ВНУТРИ нарисованного — дыра: соседей заполнили, а его пропустили.
  const граница = [сегодня, ...days.filter((день) => день > сегодня && расписан(rows, день))].reduce(
    (позже, день) => (день > позже ? день : позже),
  );
  const состояние = new Map<string, СостояниеДня>();
  for (const день of days) {
    if (день > граница) {
      continue;
    }
    const будущий = день > сегодня;
    let факт = 0;
    for (const строка of rows) {
      const ячейка = строка.cells.find((я) => я.work_date === день);
      // В прошедшем дне считается отработанное. В будущем отработанного нет, есть график:
      // часы, проставленные наперёд, а если их нет — план.
      const часы = ячейка?.hours ?? (будущий ? ячейка?.planned_hours : null);
      if (часы != null) {
        факт += Number(часы);
      }
    }
    состояние.set(день, { факт, норма, разница: Number((норма - факт).toFixed(2)), будущий });
  }
  return состояние;
}

/** Нарисован ли на этот день график хоть у кого-то: часы наперёд либо план. */
function расписан(rows: readonly GridRowOut[], день: string): boolean {
  return rows.some((строка) =>
    строка.cells.some(
      (я) => я.work_date === день && (я.hours != null || я.planned_hours != null),
    ),
  );
}

/** Дни, где объект не закрыт. По ним же ходит переход от плитки «Недобор». */
export function дниНедобора(состояние: СостояниеДней): string[] {
  return [...состояние.entries()]
    .filter(([, день]) => день.разница > 0)
    .map(([дата]) => дата)
    .sort();
}

export interface ColumnsOptions {
  readonly days: readonly string[];
  readonly canEdit: boolean;
  readonly blockedReason: string | null;
  readonly monthTotalMode: boolean;
  /** Что с объектом в каждый день. Пустая карта — норматива нет, красить нечего. */
  readonly состояние?: СостояниеДней;
}

export function buildColumns({
  days,
  canEdit,
  blockedReason,
  monthTotalMode,
  состояние,
}: ColumnsOptions): GridColumn<GridRowOut>[] {
  const фамилия: GridColumn<GridRowOut> = {
    id: 'employee',
    title: 'Фамилия и имя',
    width: 190,
    sticky: true,
    align: 'left',
    cell: (строка): CellState => ({
      text: строка.employee_name,
      behaviour: 'linked',
      reason: 'Фамилия и назначение правятся в карточке человека, не в табеле.',
    }),
    footer: (): CellState => ({ text: 'Итого по объекту', behaviour: 'computed' }),
  };

  const пост: GridColumn<GridRowOut> = {
    id: 'post',
    title: 'Пост',
    width: 120,
    align: 'left',
    cell: (строка): CellState => ({
      text: строка.post_name,
      behaviour: 'linked',
      reason: 'Пост меняется переводом в карточке человека.',
    }),
  };

  const итогСтроки: GridColumn<GridRowOut> = {
    id: 'total',
    title: 'Итог',
    subtitle: 'часов',
    width: 78,
    align: 'right',
    headClassName: 'bg-bg-weak-50',
    cell: (строка): CellState => ({
      text: formatHours(строка.total_hours),
      behaviour: 'computed',
      className: 'font-medium',
    }),
    footer: (строки): CellState => ({
      text: итогЧасов(строки.map((строка) => строка.total_hours)),
      behaviour: 'computed',
    }),
  };

  if (monthTotalMode) {
    // Основной режим заказчика: одно число на человека за месяц. Так ведутся 59 объектов из
    // 60, и городить ради них сетку на тридцать один столбец незачем.
    const заМесяц: GridColumn<GridRowOut> = {
      id: 'month-total',
      title: 'Часов за месяц',
      subtitle: 'вводится',
      width: 140,
      align: 'right',
      cell: (строка): CellState => {
        if (!canEdit) {
          return {
            text: formatHours(строка.month_total_hours),
            behaviour: 'blocked',
            reason: blockedReason ?? 'Период закрыт.',
          };
        }
        return {
          text: formatHours(строка.month_total_hours),
          behaviour: 'input',
          raw: строка.month_total_hours ?? '',
        };
      },
      footer: (строки): CellState => ({
        text: итогЧасов(строки.map((строка) => строка.month_total_hours)),
        behaviour: 'computed',
      }),
    };
    return [фамилия, пост, заМесяц];
  }

  const дни: GridColumn<GridRowOut>[] = days.map((день) => {
    const дата = new Date(`${день}T00:00:00`);
    const выходной = дата.getDay() === 0 || дата.getDay() === 6;
    const это = состояние?.get(день);
    // Три состояния дня, и они разные по смыслу. Не закрыт — дыра в охране объекта, и это
    // тревога. Переработка — тоже расхождение, но не дыра: людям переплатят, а не объект
    // остался без поста, — поэтому она предупреждает, а не кричит. Сошлось — молчим.
    const неЗакрыт = это !== undefined && это.разница > 0;
    const переработка = это !== undefined && это.разница < 0;
    const тонДня = неЗакрыт ? 'ndobor' : переработка ? 'pererabotka' : null;
    return {
      id: день,
      title: String(dayNumber(день)),
      subtitle: ДНИ_НЕДЕЛИ[дата.getDay()],
      width: 44,
      align: 'center',
      // Контур на весь столбец — ровно то, о чём просил заказчик: «подсвечивать красным
      // цветом, по контуру». Столбец, а не отдельная ячейка, потому что не закрыт ОБЪЕКТ:
      // какая из тринадцати строк недодала, из данных не следует, когда график вели руками.
      // ⚠️ Цвет текста на залитой ячейке берётся из ТЕМЫ, а не из палитры тревоги. Красный
      // по светло-красному даёт 1,39:1 на тёмной теме — надпись пропадает совсем (поймано
      // сторожем `e2e/тёмная-тема`). Тревогу здесь несёт ЗАЛИВКА, а число обязано читаться:
      // это день месяца, по нему и ищут.
      headClassName: cn(
        выходной && тонДня === null && 'text-text-sub-600',
        тонДня === 'ndobor' && 'bg-error-lighter font-medium text-text-strong-950',
        тонДня === 'pererabotka' && 'bg-warning-lighter font-medium text-text-strong-950',
      ),
      hint:
        это === undefined
          ? undefined
          : неЗакрыт
            ? это.будущий
              ? `${датаСловами(день)}: по графику ${formatHours(String(это.факт))} из ${formatHours(String(это.норма))} — не хватает ${formatHours(String(это.разница))} часов`
              : `${датаСловами(день)}: вышло ${formatHours(String(это.факт))} из ${formatHours(String(это.норма))} — пост не закрыт ${formatHours(String(это.разница))} часов`
            : переработка
              ? `${датаСловами(день)}: ${это.будущий ? 'по графику' : 'вышло'} ${formatHours(String(это.факт))} из ${formatHours(String(это.норма))} — переработка ${formatHours(String(-это.разница))} часов`
              : undefined,
      cell: (строка): CellState => {
        if (!вСрокеНазначения(строка, день)) {
          return {
            text: '',
            behaviour: 'blocked',
            reason:
              день < строка.date_from
                ? `Человек заступил ${датаСловами(строка.date_from)}: раньше его на объекте не было.`
                : `Человек уехал ${датаСловами(строка.date_to ?? день)}: позже его на объекте не было.`,
            className: 'bg-bg-weak-50',
          };
        }
        const ячейка = ячейкаДня(строка, день);
        const отметка = ячейка?.mark ?? 'worked';
        if (!canEdit) {
          return {
            text: содержимое(ячейка),
            behaviour: 'blocked',
            reason: blockedReason ?? 'Период закрыт.',
          };
        }
        // Виновник обводится ТОЛЬКО когда он доказуем: есть план и факт меньше плана, либо
        // стоит отметка невыхода. Угадывать по соседним дням нельзя — ложный красный приучает
        // не смотреть на красное вообще, и тогда пропадёт и настоящий.
        const виноват =
          неЗакрыт &&
          (отметка !== 'worked' ||
            (ячейка?.planned_hours != null &&
              Number(ячейка.hours ?? 0) < Number(ячейка.planned_hours)));
        return {
          text: содержимое(ячейка),
          behaviour: 'input',
          raw: ячейка?.hours ?? '',
          // План виден бледным, пока факта нет: человек различает «по графику должен выйти» и
          // «вышел». Без этого сгенерированный месяц читался бы как отработанный.
          className: cn(
            отметка !== 'worked'
              ? 'text-warning-base'
              : ячейка?.hours === null && ячейка?.planned_hours !== null
                ? 'text-text-sub-600'
                : undefined,
            // Заливка на весь столбец: без неё контур разрывается посередине, и глаз не
            // связывает красную шапку с красным итогом через тринадцать белых строк.
            тонДня === 'ndobor' && 'bg-error-lighter',
            тонДня === 'pererabotka' && 'bg-warning-lighter',
            виноват && 'ring-1 ring-inset ring-error-base',
          ),
        };
      },
      footer: (строки): CellState => ({
        text: итогЧасов(строки.map((строка) => ячейкаДня(строка, день)?.hours)),
        behaviour: 'computed',
        // Итог дня — то место, где расхождение ВИДНО ЧИСЛОМ: 18 против 24. Здесь контур и
        // замыкается снизу, а число красится в тот же тон, что и шапка.
        className: cn(
          тонДня === 'ndobor' && 'bg-error-lighter font-medium text-text-strong-950',
          тонДня === 'pererabotka' && 'bg-warning-lighter font-medium text-text-strong-950',
        ),
      }),
    };
  });

  return [фамилия, пост, ...дни, итогСтроки];
}

/** Что стоит в ячейке: часы, буква отметки или план бледным. */
function содержимое(ячейка: CellOut | undefined): string {
  if (ячейка === undefined) {
    return '';
  }
  if (ячейка.mark !== 'worked') {
    return MARK_LETTER[ячейка.mark] ?? '';
  }
  if (ячейка.hours !== null) {
    return formatHours(ячейка.hours);
  }
  return ячейка.planned_hours === null ? '' : formatHours(ячейка.planned_hours);
}

function dayNumber(день: string): number {
  return Number(день.slice(8, 10));
}

function датаСловами(день: string): string {
  return `${день.slice(8, 10)}.${день.slice(5, 7)}`;
}
